import { Checkbox, FormControlLabel } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { Col, Cols, Row, Rows, Text, TextButton } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IMetric,
  IMetricsContent,
  IProps,
} from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/common/types';

interface IMetricColumns {
  selectedMetricsForExistingReport: string[];
  metricsContents: IMetric;
  handleChangeMetrics: (list: IMenuList) => void;
  isClear: boolean;
}

interface ICollapsibleColumns {
  list: IMenuList;
  title: string;
  handleChangeMetrics: (list: IMenuList) => void;
  isClear: boolean;
  selectedMetricsForExistingReport: string[];
}

interface IMetricsCheckbox {
  list: IMenuList;
  title: string;
  handleChangeMetricsList: (list: IMenuList) => void;
}

const getNumberOfCheckedMetrics = (list: IMenuList) => {
  let count = 0;
  Object.values(list).forEach((metric) => {
    if (metric.selected) {
      count++;
    }
  });
  return count;
};

const MetricsCheckbox = (checkboxProps: IMetricsCheckbox) => {
  return (
    <Rows>
      {Object.values(checkboxProps.list).map((metric: any) => (
        <Row noVerticalPadding horizontalPadding>
          <Cols style={{ alignItems: 'center' }}>
            <Col>
              <Checkbox
                size='small'
                checked={metric.selected}
                onChange={() => {
                  const newIMenulist = cloneDeep(checkboxProps.list);
                  newIMenulist[metric.ID].selected = !metric.selected;
                  checkboxProps.handleChangeMetricsList(newIMenulist);
                }}
                style={{ width: '10px', marginRight: '4px' }}
              />
            </Col>
            <Col>
              <Text small>{metric.title}</Text>
            </Col>
          </Cols>
        </Row>
      ))}
    </Rows>
  );
};

const CollapsibleColumns = (collapsibleColProps: ICollapsibleColumns) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [metricsList, setMetricsList] = useState<IMenuList>(collapsibleColProps.list);

  // to update the selected metrics in the parent component
  useEffect(() => {
    collapsibleColProps.handleChangeMetrics(metricsList);
  }, [metricsList]);

  // to handle the Clear button
  useEffect(() => {
    handleSelectMetricsListByType(false);
    setIsSelected(false);
  }, [collapsibleColProps.isClear]);

  // select the checkbox for the metrics already selected for the existing report
  useEffect(() => {
    const updatedMetrics = cloneDeep(metricsList);
    collapsibleColProps.selectedMetricsForExistingReport.forEach((metric) => {
      if (updatedMetrics[metric]) {
        updatedMetrics[metric].selected = true;
      }
    });
    setMetricsList(updatedMetrics);
  }, [collapsibleColProps.selectedMetricsForExistingReport]);

  // update the metrics list, when clicking on the individual checkbox
  const handleChangeMetricsList = (list: IMenuList) => {
    setMetricsList(list);
  };

  const handleExpandMetrics = () => {
    setIsExpanded(!isExpanded);
  };

  // update the metrics list, when clicking on the checkbox next to the header, to select all metrics at ones
  const handleSelectMetricsListByType = (isSelected: boolean) => {
    const updatedMetrics = cloneDeep(metricsList);
    Object.values(updatedMetrics).forEach((metric) => {
      metric.selected = isSelected;
    });
    setMetricsList(updatedMetrics);
  };

  const numberOfCheckedMetrics = getNumberOfCheckedMetrics(metricsList);
  const length = Object.values(metricsList).length;

  return (
    <Rows>
      <Row noPadding>
        <Cols style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
          <TextButton
            onClick={handleExpandMetrics}
            style={{
              marginRight: '5px',
              minWidth: '20px',
              fontSize: '20px',
            }}
          >
            {isExpanded ? ' - ' : ' + '}
          </TextButton>
          <Col>
            <FormControlLabel
              label={
                <Text small bold>
                  {collapsibleColProps.title}
                </Text>
              }
              control={
                <Checkbox
                  size='small'
                  checked={numberOfCheckedMetrics === length}
                  indeterminate={0 < numberOfCheckedMetrics && numberOfCheckedMetrics < length}
                  onChange={() => {
                    setIsSelected(!isSelected);
                    handleSelectMetricsListByType(!isSelected);
                  }}
                />
              }
            />
          </Col>
        </Cols>
      </Row>
      <Row noVerticalPadding horizontalPadding>
        {isExpanded && (
          <MetricsCheckbox
            list={metricsList}
            title={collapsibleColProps.title}
            handleChangeMetricsList={handleChangeMetricsList}
          />
        )}
      </Row>
    </Rows>
  );
};

const MetricColumns = (colProps: IMetricColumns) => {
  // to divide the metrics by type in columns
  const firstCol: IMetricsContent[] = [];
  const secondCol: IMetricsContent[] = [];
  const thirdCol: IMetricsContent[] = [];
  const forthCol: IMetricsContent[] = [];

  Object.values(colProps.metricsContents).forEach((metrics, i: number) => {
    if (i % 4 === 0) {
      firstCol.push(metrics);
    } else if (i % 4 === 1) {
      secondCol.push(metrics);
    } else if (i % 4 === 2) {
      thirdCol.push(metrics);
    } else {
      forthCol.push(metrics);
    }
  });
  return (
    <Cols wrap responsive>
      {[firstCol, secondCol, thirdCol, forthCol].map((column) => {
        return (
          <Col>
            <Rows>
              {column.map((metric) => {
                return (
                  <Row noHorizontalPadding extraSmallVerticalPadding style={{ minWidth: '280px' }}>
                    <CollapsibleColumns
                      list={metric.data}
                      title={metric.type}
                      handleChangeMetrics={colProps.handleChangeMetrics}
                      isClear={colProps.isClear}
                      selectedMetricsForExistingReport={colProps.selectedMetricsForExistingReport}
                    />
                  </Row>
                );
              })}
            </Rows>
          </Col>
        );
      })}
    </Cols>
  );
};

export const MetricsRow = (props: IProps) => {
  const [isClear, setIsClear] = useState<boolean>(false);
  return (
    <Rows paddingBottom='16px'>
      <Row noPadding paddingBottom='8px'>
        <TextButton
          size='small'
          onClick={() => {
            setIsClear(!isClear);
            props.handleClearMetrics();
          }}
        >
          Clear
        </TextButton>
      </Row>
      <Row noPadding>
        <MetricColumns
          selectedMetricsForExistingReport={props.selectedMetricsForExistingReport}
          metricsContents={props.allMetricsContent}
          handleChangeMetrics={props.handleChangeMetrics}
          isClear={isClear}
        />
      </Row>
    </Rows>
  );
};
